'use client'

import { Box } from '@/styled-system/jsx'
import type { HTMLStyledProps } from '@/styled-system/types'
import { formatShort } from '@/utils/formatNumber'
import { forwardRef, useMemo } from 'react'
import Chip from './chip'

const MAX_STACK_SIZE = 3

export type ChipStackProps = HTMLStyledProps<'div'> & {
  amount?: number
}

const ChipStack = forwardRef<HTMLDivElement, ChipStackProps>(function ChipStack(
  { amount = 0, ...props },
  ref
) {
  const chips = useMemo(() => {
    const chips = []

    let restOfAmount = amount

    while (restOfAmount > 0 && chips.length < MAX_STACK_SIZE) {
      const power = Math.floor(Math.log10(restOfAmount))
      const value = 10 ** power
      const n = Math.min(
        Math.floor(restOfAmount / value),
        MAX_STACK_SIZE - chips.length
      )
      for (let i = 0; i < n; i++) {
        chips.push(value)
      }
      restOfAmount -= n * value
    }

    return chips
  }, [amount])

  const renderText = formatShort(amount)

  return (
    <Box ref={ref} position='relative' pointerEvents='none' {...props}>
      {chips.map((value, index) => (
        <Box
          key={index}
          position={index === 0 ? 'relative' : 'absolute'}
          top={0}
          left={0}
          fontSize='0.7em'
          style={{
            transform: `translateY(-${15 * index}%)`,
          }}
        >
          <Chip value={value}>{renderText}</Chip>
        </Box>
      ))}
    </Box>
  )
})

export default ChipStack
